import {
  Box,
  Dialog,
  IconButton,
  TextField,
  Typography,
  useTheme,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Button from '@mui/material/Button';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import {
  GetLocationReportInfoDocument,
  LocationReportInfoResponse,
  useEditLocationReportInfoReplyMutation,
} from '../../generated/graphql';

interface ReportToTerminalEditReplyModalProps {
  handleClose: () => void;
  open: boolean;
  internalTerminalReportInfo: Pick<
    LocationReportInfoResponse,
    | 'id'
    | 'info'
    | 'infoType'
    | 'locationId'
    | 'dateForAddInfo'
    | 'isAddedFromTerminal'
    | 'replyInfo'
    | 'replyText'
  >;
}

export function ReportToTerminalEditReplyToInfoModal(
  props: ReportToTerminalEditReplyModalProps,
) {
  const { open, handleClose, internalTerminalReportInfo } = props;
  const { t } = useTranslation();
  const {
    id: locationReportInfoId,
    info,
    infoType,
    locationId,
    dateForAddInfo,
    isAddedFromTerminal,
    replyInfo,
    replyText,
  } = internalTerminalReportInfo;

  const theme = useTheme();
  const lessThan1400 = useMediaQuery(theme.breakpoints.between(0, 1400));

  const [editReplyToInfoMutation] = useEditLocationReportInfoReplyMutation({
    refetchQueries: [
      {
        query: GetLocationReportInfoDocument,
        variables: {
          locationId,
          dateForAddInfo,
          forTerminal: isAddedFromTerminal,
        },
      },
    ],
  });

  const quickResponses = [
    t('quickResponse.ok'),
    t('quickResponse.weAreLooking'),
    t('quickResponse.thankYou'),
  ];

  if (!internalTerminalReportInfo) {
    return <></>;
  }

  return (
    <Box>
      <Dialog
        maxWidth={lessThan1400 ? 'sm' : 'md'}
        fullWidth={true}
        open={open}
        onClose={handleClose}
      >
        <Formik
          initialValues={{
            locationReportInfoId,
            info,
            infoType,
            locationId,
            dateForAddInfo,
            replyInfo: replyInfo ?? '',
            replyText: replyText ?? '',
            quickResponse: '',
          }}
          onSubmit={async (submit_val: any) => {
            editReplyToInfoMutation({
              variables: {
                input: {
                  locationReportInfoId: submit_val.locationReportInfoId,
                  replyText: submit_val.replyText,
                },
              },
            });
            handleClose();
          }}
        >
          {(props) => (
            <form onSubmit={props.handleSubmit}>
              <DialogTitle>
                <Box
                  display="flex"
                  flexDirection="row"
                  justifyContent="space-between"
                  width="100%"
                  mb={2}
                >
                  <Typography variant="body1">
                    <strong>{t('attributes.replyToInfo')}</strong>
                  </Typography>
                  <Typography variant="body1">{``}</Typography>
                  <IconButton style={{ padding: '0px' }} onClick={handleClose}>
                    <CloseIcon fontSize="medium" color="action" />
                  </IconButton>
                </Box>
              </DialogTitle>
              <DialogContent>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'flex-start',
                    paddingBottom: '2%',
                  }}
                >
                  <Typography variant="body1">{`${props.values.info}`}</Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    paddingTop: '2%',
                  }}
                >
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel id="quick-response-label">
                      {t('attributes.quickResponse')}
                    </InputLabel>
                    <Select
                      labelId="quick-response-label"
                      id="quickResponse"
                      value={props.values.quickResponse}
                      label={t('attributes.quickResponse')}
                      onChange={(e) => {
                        props.setFieldValue('quickResponse', e.target.value);
                        props.setFieldValue('replyText', e.target.value);
                      }}
                    >
                      {quickResponses.map((resp) => (
                        <MenuItem value={resp} key={resp}>
                          {resp}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    label="Reply"
                    id="replyText"
                    multiline
                    rows={3}
                    variant="outlined"
                    value={props.values.replyText}
                    onChange={props.handleChange}
                  />
                </Box>
              </DialogContent>
              <DialogActions
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'center',
                  paddingBottom: '1%',
                }}
              >
                <Box>
                  <Button type="submit" variant="contained">
                    {t('button.save')}
                  </Button>
                </Box>
              </DialogActions>
            </form>
          )}
        </Formik>
      </Dialog>
    </Box>
  );
}
